import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Close as CloseIcon } from '@mui/icons-material';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, FormControl, InputLabel, Select, MenuItem, Chip } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { constants } from '../constants';
import { ConfigContext } from './ConfigContext';
const UserGroupModal = ({ open, onClose, onSave, group, loading, 
// eslint-disable-next-line @typescript-eslint/no-unused-vars
applications, roles = [] }) => {
    const [groupName, setGroupName] = useState('');
    // Removed selectedApplication state
    const [selectedRoles, setSelectedRoles] = useState([]);
    const [error, setError] = useState(null);
    const { configState } = React.useContext(ConfigContext);
    const isEditMode = !!group; // Edit mode when group is provided
    useEffect(() => {
        if (open) {
            if (group) {
                // Edit mode - populate form with existing data
                setGroupName(group.name || '');
                // Parse role IDs from group details
                const roleIds = group.details?.roles?.map(Number) || [];
                setSelectedRoles(roleIds);
            }
            else {
                // Add mode - reset form
                setGroupName('');
                setSelectedRoles([]);
            }
            setError(null);
        }
    }, [open, group]);
    const handleSave = () => {
        if (groupName.trim() && selectedRoles.length > 0) {
            const groupData = {
                name: groupName.trim(),
                roleIds: selectedRoles
            };
            onSave(groupData);
        }
    };
    const handleRoleChange = (event) => {
        const value = event.target.value;
        setSelectedRoles(typeof value === 'string' ? [] : value);
    };
    const handleDeleteRole = (roleId) => {
        setSelectedRoles(selectedRoles.filter((id) => id !== roleId));
    };
    const canSave = groupName.trim() && selectedRoles.length > 0;
    const getButtonText = () => {
        if (loading)
            return constants.saving;
        return isEditMode
            ? constants.configuration_modal_button_update
            : constants.configuration_modal_button_add;
    };
    return (_jsxs(Dialog, { open: open, onClose: onClose, maxWidth: "sm", fullWidth: true, sx: {
            '& .MuiDialog-paper': {
                borderRadius: 2,
                boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)',
                minHeight: 400
            }
        }, children: [_jsx(DialogTitle, { variant: "h6", sx: {
                    color: '#212121',
                    borderBottom: '1px solid #ebebeb'
                }, children: isEditMode
                    ? constants.usergroup_modal_title_edit(configState?.settings?.workspace_name ??
                        constants.workspace_default_name)
                    : constants.usergroup_modal_title_add(configState?.settings?.workspace_name ??
                        constants.workspace_default_name) }), _jsx(DialogContent, { sx: { p: '24px !important' }, children: _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 }, children: [_jsx(TextField, { fullWidth: true, size: "small", label: constants.usergroup_modal_field_group_name, value: groupName, onChange: (e) => setGroupName(e.target.value), required: true }), _jsxs(FormControl, { fullWidth: true, size: "small", children: [_jsx(InputLabel, { id: "role-select-label", children: constants.usergroup_modal_field_roles }), _jsx(Select, { labelId: "role-select-label", multiple: true, value: selectedRoles, onChange: handleRoleChange, label: constants.usergroup_modal_field_roles, renderValue: () => constants.usergroup_modal_select, children: roles.map((role) => (_jsx(MenuItem, { value: Number(role.id), children: role.name }, role.id))) })] }), error && (_jsx(Alert, { severity: "error", sx: { mt: 1 }, children: error })), selectedRoles.length > 0 && (_jsx(Box, { sx: { display: 'flex', flexWrap: 'wrap', gap: 1 }, children: selectedRoles.map((roleId) => {
                                const role = roles.find((r) => Number(r.id) === Number(roleId));
                                return role ? (_jsx(Chip, { label: role.name, onDelete: () => handleDeleteRole(roleId), deleteIcon: _jsx(CloseIcon, {}), sx: {
                                        backgroundColor: '#f3f8eb',
                                        color: '#1a5d09',
                                        fontSize: 12,
                                        fontWeight: 600,
                                        height: 24,
                                        '& .MuiChip-deleteIcon': {
                                            color: '#1a5d09',
                                            fontSize: 16,
                                            '&:hover': {
                                                color: '#26890d'
                                            }
                                        }
                                    } }, roleId)) : null;
                            }) }))] }) }), _jsxs(DialogActions, { sx: {
                    px: 3,
                    pb: 2,
                    pt: 1,
                    gap: 2,
                    borderTop: '1px solid #ebebeb'
                }, children: [_jsx(Button, { onClick: onClose, disabled: loading, sx: {
                            color: '#26890d',
                            textTransform: 'none',
                            fontFamily: 'Open Sans',
                            fontWeight: 600,
                            fontSize: 16,
                            px: 2,
                            py: 1,
                            '&:hover': {
                                backgroundColor: 'rgba(38, 137, 13, 0.04)'
                            }
                        }, children: constants.cancel }), _jsx(Button, { onClick: handleSave, variant: "contained", disabled: loading || !canSave, sx: {
                            backgroundColor: canSave ? '#26890d' : '#e0e0e0',
                            color: canSave ? '#ffffff' : '#999',
                            textTransform: 'none',
                            fontFamily: 'Open Sans',
                            fontWeight: 600,
                            fontSize: 16,
                            px: 3,
                            py: 1,
                            '&:hover': {
                                backgroundColor: canSave ? '#1f6b0a' : '#e0e0e0'
                            },
                            '&:disabled': {
                                backgroundColor: '#e0e0e0',
                                color: '#999'
                            }
                        }, children: getButtonText() })] })] }));
};
export default UserGroupModal;
